import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import Button from 'components/crm/ui/Button';
import Drawer from 'components/crm/ui/Drawer';
import PillTabs from 'components/crm/ui/PillTabs';
import Spinner from 'components/crm/ui/Spinner';
import TextField from 'components/crm/ui/TextField';
import EmptyState from 'components/crm/ui/emptyState/EmptyState';
import DrawnGlyph from 'components/crm/ui/emptyState/DrawnGlyph';
import useText from 'hooks/useText';
import {
  filterProspects,
  globalTabs,
  inDayValues,
} from 'pages/prospection/callListItems';
import GlobalListRow from './GlobalListRow';

/**
 * The global list, in a Drawer (`open`, `onClose`, its « Ajouter » through
 * `onAdd`). `inline`: in the page instead, read-only (another's list, seen by
 * the agency manager): no add, no footer, and `counts` may be missing (his day
 * not opened yet).
 */
const GlobalListPanel = ({
  open,
  onClose,
  prospects,
  loading,
  counts,
  onAdd,
  onShowAllProspects,
  inline = false,
}) => {
  const { text } = useText('prospection');
  const [tab, setTab] = useState('discovery');
  const [query, setQuery] = useState('');
  const [addedIds, setAddedIds] = useState(() => new Set());
  // Read at opening only: adding a prospect changes the counts, and the tab
  // must not jump under the user's hands.
  const countsRef = useRef(counts);
  countsRef.current = counts;
  // The page mounts the panel before the day has loaded, so counts may be
  // missing: the opening reset waits for them.
  const countsLoaded = Boolean(counts);

  const shown = inline || open;

  useEffect(() => {
    if (!shown || !countsLoaded) return;
    setTab(globalTabs(countsRef.current).initialTab);
    setQuery('');
    setAddedIds(new Set());
  }, [shown, countsLoaded]);

  const handleAdd = useCallback(
    async (prospect) => {
      await onAdd(prospect);
      setAddedIds((previous) => new Set(previous).add(prospect.company.id));
    },
    [onAdd],
  );

  const visible = useMemo(
    () => filterProspects(prospects, { type: tab, query }),
    [prospects, tab, query],
  );

  if (!shown || (!counts && !inline)) return null;

  // Proposable rows of a type: those already moved to "Added" here no longer
  // count. Undefined while loading, so the kit shows no badge.
  const proposable = (type) =>
    loading
      ? undefined
      : (prospects || []).filter(
          (prospect) =>
            prospect.type === type && !addedIds.has(prospect.company.id),
        ).length;
  const tabs = [
    { id: 'discovery', label: text('global.tabs.discovery'), count: proposable('discovery') },
    { id: 'followup', label: text('global.tabs.followup'), count: proposable('followup') },
  ];

  const renderList = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-10" role="status">
          <Spinner />
        </div>
      );
    }
    if (visible.length === 0) {
      return query.trim() ? (
        <EmptyState
          compact
          glyph={<DrawnGlyph name="searchX" />}
          title={text('global.emptySearchTitle')}
          description={text('global.emptySearchText')}
        />
      ) : (
        <EmptyState
          compact
          glyph={<DrawnGlyph name="squareCheck" />}
          title={text(`global.emptyTab.${tab}`)}
          description={text('global.emptyTabText')}
        />
      );
    }
    return (
      <ul className="flex flex-col">
        {visible.map((prospect) => (
          <GlobalListRow
            key={prospect.company.id}
            prospect={prospect}
            added={addedIds.has(prospect.company.id)}
            onAdd={inline ? undefined : handleAdd}
          />
        ))}
      </ul>
    );
  };

  const inDay = counts && (
    <p className="text-sm text-muted">
      {text('global.inDay', inDayValues(counts))}
    </p>
  );
  const filters = (
    <div className="flex flex-col gap-3">
      <PillTabs tabs={tabs} value={tab} onChange={setTab} />
      <TextField
        id={inline ? 'global-list-search-inline' : 'global-list-search'}
        type="search"
        icon={Search}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={text('global.search')}
        aria-label={text('global.search')}
      />
    </div>
  );

  if (inline) {
    return (
      <section
        data-testid="global-list-inline"
        className="flex flex-col gap-4"
      >
        <h2 className="font-title text-sm font-medium text-ink">
          {text('global.title')}
        </h2>
        {inDay}
        {filters}
        {renderList()}
      </section>
    );
  }

  return (
    <Drawer
      open
      onClose={onClose}
      title={text('global.title')}
      description={text('global.description')}
      footer={
        <>
          <span className="flex-1" />
          <Button variant="secondary" onClick={onShowAllProspects}>
            {text('global.showAll')}
          </Button>
          <Button onClick={onClose}>{text('global.done')}</Button>
        </>
      }
    >
      {inDay}
      {filters}
      {renderList()}
    </Drawer>
  );
};

export default GlobalListPanel;
